import { PureQueryOptions } from '@apollo/client';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { LinkButton, theme, useToast, MailOpenedIcon } from '@arnold/common';
import { Table } from '../../components/Table/Table';
import { IsAccessTokenValidQuery, ReportAccessQuery, SequenceReportAccessesQuery } from '../../generated/hooks';
import { getDataSource } from './getDataSource';
import { ReportAccessDetail } from './ReportAccessDetail';
import { ReportData, SurveyTableItemType } from './types';
import { combineSurveysAndSequences, getColumns, useAcceptReports } from './utils';

interface IProps {
  dependantQueries: Array<string | PureQueryOptions>;
  showOpenIcon?: boolean;
  surveys?: ReportAccessQuery['survey'][];
  surveyGroups?: SequenceReportAccessesQuery['surveyGroup'][];
  showAdditionalInfoInTable?: boolean;
  user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']['user']>;
}

const BulkActionsWrap = styled.div`
  padding-left: ${theme.spacing.k};
`;

const Container = styled.div`
  margin-bottom: ${theme.spacing.f};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledMailOpenedIcon = styled(MailOpenedIcon)`
  margin-right: ${theme.spacing.c};
`;

const OpenRate = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

export const ReportAccessTable: FC<IProps> = ({
  dependantQueries,
  showOpenIcon,
  surveys,
  surveyGroups,
  showAdditionalInfoInTable,
  user,
}) => {
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [t] = useTranslation('ReportAccess');
  const { addToast } = useToast();
  const acceptReports = useAcceptReports(
    t,
    addToast,
    () => setSelectedReports([]),
    selectedReports,
    dependantQueries,
    true,
    user,
  );
  const suspendReports = useAcceptReports(
    t,
    addToast,
    () => setSelectedReports([]),
    selectedReports,
    dependantQueries,
    false,
    user,
  );

  const toggleReport = (id: string | string[]) => {
    if (Array.isArray(id)) {
      setSelectedReports(id);
      return;
    }

    const selectedIds = selectedReports.slice();
    const reportIndex = selectedReports.indexOf(id);
    if (reportIndex !== -1) {
      selectedIds.splice(reportIndex, 1);
    } else {
      selectedIds.push(id);
    }

    setSelectedReports(selectedIds);
  };

  const tableItems = combineSurveysAndSequences(surveys || [], surveyGroups || []);

  const isOngoing = tableItems.every((item) => item.isOngoing);
  const isReportPendingApproval = Boolean(surveys?.some((survey) => survey?.isReportPendingApproval));

  const allSentReports = tableItems
    .map((item) => item.data?.reports)
    .flat()
    .filter((report) => report?.respondent && report.sent);
  const opened = allSentReports.filter((report) => report?.readAt).length;

  const hasUnreadInfo = tableItems.some(
    (item) =>
      item.type === SurveyTableItemType.Sequence &&
      item.data?.reports?.some((report) => report?.unreadCount || report?.totalCount),
  );

  return (
    <>
      <Container>
        {dependantQueries.length > 0 && !isReportPendingApproval && (
          <BulkActionsWrap>
            <LinkButton
              disabled={!selectedReports.length}
              onClick={() => acceptReports()}
              data-icom={'btn-accept-reports'}
            >
              {t(isOngoing ? 'acceptAllOngoing' : 'acceptAll')}
            </LinkButton>
            <LinkButton
              disabled={!selectedReports.length}
              onClick={() => suspendReports()}
              data-icom={'btn-suspend-reports'}
            >
              {t('rejectAll')}
            </LinkButton>
          </BulkActionsWrap>
        )}
        <OpenRate>
          <StyledMailOpenedIcon />
          {t('openReportRate', {
            opened,
            total: allSentReports.length,
            percentage: allSentReports.length === 0 ? 0 : Math.round((opened / allSentReports.length) * 100),
          })}
        </OpenRate>
      </Container>
      <Table<ReportData>
        columns={getColumns(
          t,
          showOpenIcon,
          showAdditionalInfoInTable,
          !!tableItems.find((item) => item.type === SurveyTableItemType.Sequence),
          hasUnreadInfo,
        )}
        dataSources={getDataSource({
          t,
          showOpenIcon,
          tableItems,
          dependantQueries,
          user,
          isReportPendingApproval,
        })}
        selected={undefined}
        renderDetailPanel={(rowData) => <ReportAccessDetail rowData={rowData} />}
        rowSelection={
          isReportPendingApproval
            ? undefined
            : {
                selected: selectedReports,
                onChange: toggleReport,
              }
        }
      />
    </>
  );
};
